/**
 * OnboardingDataPageV2 — redesigned "Tapping Basics" editor.
 *
 * Three parallel video slots — each with a title input, a VideoTile
 * uploader, and a computed-duration sub-line. The mobile app's
 * onboarding plays these in order, so the three slots are visually
 * symmetrical (not numbered FormSections — they're peers, not steps).
 *
 * Layout: 1-col on narrow, 3-col on lg+. Sticky PageHeader with a
 * single coral Save button that fires the hidden form's submit
 * handler (same plumbing as v1).
 *
 * Same Redux + apiV2 plumbing as v1: `fetchOnboardingData`,
 * `updateOnboardingData`, `uploadVideo`, `clearMedia`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  fetchOnboardingData,
  updateOnboardingData,
} from '../../../../actions/onboardingAction';
import { uploadVideo, clearMedia } from '../../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { PageHeader, Button } from '../../../redesign';
import {
  FieldBlock,
  TextInput,
  VideoTile,
} from '../../../redesign/edit';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const SLOTS = [
  { key: 'one',   ordinal: 1 },
  { key: 'two',   ordinal: 2 },
  { key: 'three', ordinal: 3 },
];

const EMPTY_FORM = SLOTS.reduce(
  (acc, { key }) => ({
    ...acc,
    [`onboarding_video_${key}_url`]: '',
    [`onboarding_video_${key}_title`]: '',
    [`video_${key}_onboarding_length`]: '',
    [`video_${key}_onboarding_length_in_sec`]: 0,
    [`onboarding_video_${key}_uploading`]: false,
  }),
  {},
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Wrap a video URL in a hidden <video> element and resolve once the
// duration is known. Matches the v1 metadata-derivation logic.
function probeDuration(url) {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.src = url;
    v.onloadedmetadata = () => {
      resolve({
        lengthInSec: Math.round(v.duration),
        length: formatDuration(v.duration),
      });
    };
    v.onerror = () => resolve(null);
  });
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function OnboardingDataPageV2() {
  const dispatch = useDispatch();
  const video = useSelector((s) => s.media.video);
  const onboardingData = useSelector(
    (s) => s.onboarding_data.onboarding_data,
  );

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const formRef = useRef(null);
  // Tracks which slot's upload is currently in flight so the
  // "video URL settled in Redux" effect knows which slot to write
  // into without having to read the latest formData (which would
  // make formData a dep and re-run the effect on every keystroke).
  const uploadingSlotRef = useRef(null);

  /* ─── Fetch on mount ──────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        await dispatch(fetchOnboardingData());
      } catch {
        Swal.fire({ icon: 'error', title: 'Error loading data' });
      } finally {
        setLoading(false);
      }
    })();
  }, [dispatch]);

  /* ─── Hydrate form once Redux has the row, also derive durations
   *     for already-stored URLs (server doesn't persist them). */
  useEffect(() => {
    if (!onboardingData) return;
    let cancelled = false;
    (async () => {
      const durations = await Promise.all(
        SLOTS.map(({ key }) =>
          probeDuration(onboardingData[`onboarding_video_${key}_url`]),
        ),
      );
      if (cancelled) return;
      setFormData((prev) => {
        const next = { ...prev };
        SLOTS.forEach(({ key }, idx) => {
          next[`onboarding_video_${key}_url`] =
            onboardingData[`onboarding_video_${key}_url`] || '';
          next[`onboarding_video_${key}_title`] =
            onboardingData[`onboarding_video_${key}_title`] || '';
          next[`video_${key}_onboarding_length`] = durations[idx]?.length || '';
          next[`video_${key}_onboarding_length_in_sec`] =
            durations[idx]?.lengthInSec || 0;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [onboardingData]);

  /* ─── React to a finished upload from Redux media slice ──────── */
  useEffect(() => {
    if (!video) return;
    // Capture the in-flight slot via the ref instead of reading
    // formData, so this effect doesn't depend on formData.
    const slotKey = uploadingSlotRef.current;
    setIsProcessingVideo(true);
    (async () => {
      if (!slotKey) {
        // The URL landed but no slot claimed the upload — clear
        // anyway so we don't spin forever.
        dispatch(clearMedia());
        setIsProcessingVideo(false);
        return;
      }
      const probed = await probeDuration(video);
      setFormData((prev) => ({
        ...prev,
        [`onboarding_video_${slotKey}_url`]: video,
        [`onboarding_video_${slotKey}_uploading`]: false,
        [`video_${slotKey}_onboarding_length`]: probed?.length || '',
        [`video_${slotKey}_onboarding_length_in_sec`]:
          probed?.lengthInSec || 0,
      }));
      uploadingSlotRef.current = null;
      dispatch(clearMedia());
      setIsProcessingVideo(false);
    })();
  }, [video, dispatch]);

  /* ─── Handlers ────────────────────────────────────────────────── */

  const handleTitleChange = (slotKey) => (e) => {
    const v = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [`onboarding_video_${slotKey}_title`]: v,
    }));
  };

  const handleVideoUpload = (slotKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Stash the slot key on the ref so the "video URL settled"
    // effect knows where to write the result without depending
    // on formData.
    uploadingSlotRef.current = slotKey;
    setFormData((prev) => ({
      ...prev,
      [`onboarding_video_${slotKey}_uploading`]: true,
    }));
    dispatch(uploadVideo(file));
  };

  const handleVideoRemove = (slotKey) => () => {
    setFormData((prev) => ({
      ...prev,
      [`onboarding_video_${slotKey}_url`]: '',
      [`video_${slotKey}_onboarding_length`]: '',
      [`video_${slotKey}_onboarding_length_in_sec`]: 0,
    }));
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isProcessingVideo) {
      Swal.fire({
        icon: 'warning',
        title: 'Please wait',
        text: 'A video is still uploading — try again in a moment.',
      });
      return;
    }
    setIsSaving(true);

    const payload = {};
    SLOTS.forEach(({ key }) => {
      payload[`onboarding_video_${key}_title`] =
        formData[`onboarding_video_${key}_title`];
      const hasUrl = !!formData[`onboarding_video_${key}_url`];
      payload[`onboarding_video_${key}_url`] = hasUrl
        ? formData[`onboarding_video_${key}_url`]
        : '';
      payload[`video_${key}_onboarding_length`] = hasUrl
        ? formData[`video_${key}_onboarding_length`]
        : '';
      payload[`video_${key}_onboarding_length_in_sec`] = hasUrl
        ? formData[`video_${key}_onboarding_length_in_sec`]
        : 0;
    });

    try {
      const result = dispatch(updateOnboardingData(payload));
      const after = () => {
        Swal.fire({
          icon: 'success',
          title: 'Tapping Basics saved',
          timer: 1500,
          showConfirmButton: false,
        });
        dispatch(fetchOnboardingData());
        setIsSaving(false);
      };
      if (result && typeof result.then === 'function') {
        result.then(after).catch(() => {
          Swal.fire({ icon: 'error', title: 'Error saving' });
          setIsSaving(false);
        });
      } else {
        after();
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error saving' });
      setIsSaving(false);
    }
  };

  const triggerSave = () => {
    if (!formRef.current) return;
    // Mirror the v1 trigger — dispatch a synthetic submit so the
    // form's onSubmit handler runs (same pattern v1 uses).
    formRef.current.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true }),
    );
  };

  const filledCount = SLOTS.filter(
    ({ key }) => !!formData[`onboarding_video_${key}_url`],
  ).length;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Engagement & Promotions"
        title="Tapping Basics"
        total={`${filledCount} / 3 videos`}
        actions={
          <Button
            variant="coral"
            type="button"
            onClick={triggerSave}
            leadingIcon={SaveIconSvg}
            disabled={isSaving || isProcessingVideo || loading}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        }
      />

      <div style={{ padding: '0 28px 60px' }}>
        {loading ? (
          <div
            className="flex items-center justify-center text-text-muted"
            style={{ height: 256 }}
          >
            Loading Tapping Basics…
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onKeyDown={preventEnterFormSubmission}
            autoComplete="off"
          >
            <div
              className="grid grid-cols-1 lg:grid-cols-3"
              style={{ gap: 20 }}
            >
              {SLOTS.map(({ key, ordinal }) => {
                const hasUrl = !!formData[`onboarding_video_${key}_url`];
                const duration = formData[`video_${key}_onboarding_length`];
                return (
                  <section
                    key={key}
                    className="bg-surface border border-sand"
                    style={{
                      borderRadius: 12,
                      padding: 18,
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="font-condensed font-bold uppercase text-text-muted"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        marginBottom: 10,
                      }}
                    >
                      Tapping Basics Video {ordinal}
                    </div>

                    <FieldBlock
                      label="Title"
                      htmlFor={`f-tb-title-${key}`}
                      hint="Shown above the video in the app"
                    >
                      <TextInput
                        id={`f-tb-title-${key}`}
                        name={`onboarding_video_${key}_title`}
                        value={formData[`onboarding_video_${key}_title`]}
                        onChange={handleTitleChange(key)}
                      />
                    </FieldBlock>

                    <FieldBlock
                      label="Video"
                      hint={hasUrl && duration ? `Duration · ${duration}` : 'MP4 / MOV'}
                      noMargin
                    >
                      <VideoTile
                        inputId={`f-tb-video-${key}`}
                        value={formData[`onboarding_video_${key}_url`]}
                        uploading={
                          !!formData[`onboarding_video_${key}_uploading`]
                        }
                        onChange={handleVideoUpload(key)}
                        onRemove={handleVideoRemove(key)}
                      />
                    </FieldBlock>
                  </section>
                );
              })}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
